import type { CSSProperties } from "react";
import type { Icon as PhosphorIcon, IconWeight } from "@phosphor-icons/react";
import {
  ArrowCounterClockwise,
  ArrowSquareOut,
  CalendarBlank,
  Cards,
  Checks,
  Columns,
  Crosshair,
  Export,
  FileZip,
  FunnelSimple,
  GitDiff,
  HourglassMedium,
  Laptop,
  PaperPlaneRight,
  SealCheck,
  Stop,
  Table,
  TreeStructure,
} from "@phosphor-icons/react";
import {
  Archive,
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  Armchair,
  Bell,
  BookBookmark,
  BookOpen,
  Books,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  Check,
  CheckCircle,
  Circle,
  CircleDashed,
  ClockCounterClockwise,
  Copy,
  Cpu,
  Cube,
  Desktop,
  DeviceMobile,
  DotsSixVertical,
  DotsThree,
  DownloadSimple,
  Envelope,
  Eye,
  EyeSlash,
  FileDoc,
  FilePdf,
  Files,
  Fingerprint,
  Funnel,
  Gear,
  GridFour,
  House,
  IdentificationCard,
  Image,
  Info,
  Key,
  Leaf,
  LinkSimple,
  ListBullets,
  LockSimple,
  MagnifyingGlass,
  Moon,
  Mountains,
  Notebook,
  Package,
  PencilSimple,
  Plant,
  Plus,
  Prohibit,
  Robot,
  Rows,
  Ruler,
  Shield,
  ShieldCheck,
  SignIn,
  SignOut,
  Snowflake,
  Sparkle,
  SpinnerGap,
  SquaresFour,
  Stack,
  Sun,
  Swap,
  Tag,
  Trash,
  Tree,
  UploadSimple,
  User,
  UserCircle,
  UserPlus,
  Users,
  Warning,
  WarningCircle,
  Wrench,
  X,
} from "@phosphor-icons/react";

// ============================================================
// Die Symbol-Registry (Phosphor, regular — SPEC §13.1). Nach dem Muster von
// /opt/lilasprojekte/frontend/src/components/ui/Icon.tsx, mit den Namen, die der
// Katalog braucht. Ein Name ist kebab-case wie in Phosphors eigener Liste.
//
// Wer ein Symbol braucht, das hier fehlt, trägt es HIER ein (ein Import, eine
// Zeile) — mit Kommentar, wofür. Pakete P5–P7 bitten darum in docs/INTEGRATION.md,
// wenn sie die Datei nicht selbst anfassen dürfen.
// ============================================================
const REGISTRY = {
  // Navigation und Schale
  house: House,
  books: Books, // Reiter Bibliothek
  "book-open": BookOpen, // Kataloge
  "book-bookmark": BookBookmark,
  gear: Gear, // Admin
  users: Users, // Nutzerverwaltung
  user: User,
  "user-circle": UserCircle, // Profil
  "user-plus": UserPlus, // Nutzer vorab anlegen
  "identification-card": IdentificationCard,
  "sign-out": SignOut,
  "sign-in": SignIn,
  moon: Moon,
  sun: Sun,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "caret-down": CaretDown,
  "caret-up": CaretUp,
  "caret-left": CaretLeft,
  "caret-right": CaretRight,
  "dots-three": DotsThree,
  "dots-six-vertical": DotsSixVertical, // Ziehgriff (Kapitelliste, Feldliste)
  desktop: Desktop,
  "device-mobile": DeviceMobile,

  // Handlungen
  plus: Plus,
  x: X,
  check: Check,
  copy: Copy,
  pencil: PencilSimple,
  trash: Trash,
  archive: Archive,
  download: DownloadSimple,
  upload: UploadSimple,
  "magnifying-glass": MagnifyingGlass,
  funnel: Funnel,
  "arrow-clockwise": ArrowClockwise,
  "arrows-clockwise": ArrowsClockwise, // Live-Verbindung, neu verbinden
  "arrow-counter-clockwise": ArrowCounterClockwise, // Rückgängig im Panel (P5-5, Canvas)
  "arrow-square-out": ArrowSquareOut, // Original öffnen (P7-7)
  export: Export, // Knopf „Exportieren" (P5-16, Canvas)
  "paper-plane-right": PaperPlaneRight, // KI: Senden (P7-7)
  stop: Stop, // KI: Antwort abbrechen (P7-7)
  checks: Checks, // „Alle bestätigen" (P6-5)
  "funnel-simple": FunnelSimple, // Filterchips (P6-5)
  crosshair: Crosshair, // Fokuspunkt (P5-5)
  swap: Swap,
  eye: Eye,
  "eye-slash": EyeSlash,
  link: LinkSimple,
  envelope: Envelope,
  key: Key, // Maschinen-Token
  lock: LockSimple,
  prohibit: Prohibit, // gesperrt, stillgelegt
  fingerprint: Fingerprint,

  // Fachliches
  files: Files,
  "file-pdf": FilePdf,
  "file-doc": FileDoc,
  "file-zip": FileZip, // Stand als ZIP (P5-5)
  "git-diff": GitDiff, // Vergleich, Herkunft (P5-5, P7-7)
  "calendar-blank": CalendarBlank, // Stand-Datum (P5-5)
  "seal-check": SealCheck, // Abgabe (P5-5, P7-7)
  laptop: Laptop, // Ernte vom Laptop (P7-7)
  "hourglass-medium": HourglassMedium, // wartet (P7-7)
  table: Table, // Elementtabelle (P6-5)
  "tree-structure": TreeStructure, // Baum (P6-5)
  columns: Columns, // Spaltenwahl (P6-5)
  cards: Cards, // Kartenliste am Telefon (P6-5)
  image: Image,
  package: Package, // Bibliotheksprodukt
  stack: Stack, // Stände — nur dafür; der Schichtaufbau hat sein eigenes Zeichen (`aufbau`)
  aufbau: Rows, // Schichtaufbau eines Belags (§19.16): Bänder wie im Schnitt
  snowflake: Snowflake, // Stand einfrieren
  clock: ClockCounterClockwise, // Fassungen, Verlauf
  cube: Cube, // Modell (IFC-Daten; kein 3D, §17)
  "grid-four": GridFour, // Tabelle
  "squares-four": SquaresFour, // Raster
  "list-bullets": ListBullets,
  tag: Tag, // Kategorie
  ruler: Ruler, // Menge
  tree: Tree, // Vegetation
  plant: Plant,
  leaf: Leaf,
  armchair: Armchair, // Einbauten/Sitzmöbel
  mountains: Mountains, // Bodenbeläge
  notebook: Notebook,
  robot: Robot, // KI-Assistent
  sparkle: Sparkle, // Anreicherung, Vorschlag
  cpu: Cpu,
  wrench: Wrench,
  shield: Shield,
  "shield-check": ShieldCheck, // bestätigt
  bell: Bell,
  circle: Circle, // offen
  "circle-dashed": CircleDashed, // Vorschlag

  // Zustände und Meldungen
  warning: Warning,
  "warning-circle": WarningCircle,
  "check-circle": CheckCircle,
  info: Info,
  "spinner-gap": SpinnerGap,
} satisfies Record<string, PhosphorIcon>;

/** Alle Namen, die `<Icon name="…">` kennt. */
export type IconName = keyof typeof REGISTRY;
export const ICON_NAMEN = Object.keys(REGISTRY) as IconName[];

export interface IconProps {
  /** Ein Name aus der Registry. Absichtlich `string`: ein Aufrufer soll einen Namen
   *  aus einer Datenbankzeile durchreichen dürfen. */
  name: string;
  /** Strichstärke; Vorgabe "regular". Kräftige Stärken sind Ausnahmen. */
  weight?: IconWeight;
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** wird zum barrierefreien Namen; ohne ihn ist das Symbol reine Zier. */
  title?: string;
}

export function Icon({ name, weight = "regular", size = 18, className = "", style = {}, title }: IconProps) {
  const Cmp = (REGISTRY as Record<string, PhosphorIcon | undefined>)[name];

  if (!Cmp) {
    // Ein vertippter Name fällt nicht beim Übersetzen auf, sondern als leere Stelle im
    // Bild. In der Entwicklung deshalb einmal laut werden; im Bau bleibt ein
    // maßhaltiger, unsichtbarer Platzhalter, damit die Zeile nicht zusammenrutscht.
    if (import.meta.env.DEV) console.warn(`[Icon] Name nicht in der Registry: ${name}`);
    return <span aria-hidden style={{ width: size, height: size, display: "inline-block", verticalAlign: "middle", flex: "none" }} />;
  }

  return (
    <Cmp
      size={size}
      weight={weight}
      className={className}
      style={{ verticalAlign: "middle", flex: "none", ...style }}
      aria-hidden={title ? undefined : true}
      alt={title}
    />
  );
}
